import React from 'react';
import moment from 'moment';
import {
  DateInput as BPDateInput,
  DateRangeInput as BPDateRangeInput,
} from '@blueprintjs/datetime';
import { DateInput as BPFormikDateInput } from '@blueprintjs-formik/datetime';

const FRENCH_DATE_FORMAT = 'DD/MM/YYYY';

const m = (date: any, locale: any = 'fr') => moment(date).locale(locale);

const MomentLocaleUtils = {
  formatDay: (day: any, locale: any = 'fr') => m(day, locale).format('ddd ll'),
  formatMonthTitle: (date: any, locale: any = 'fr') =>
    m(date, locale).format('MMMM YYYY'),
  formatWeekdayShort: (weekday: any, locale: any = 'fr') =>
    moment().locale(locale).localeData().weekdaysMin()[weekday],
  formatWeekdayLong: (weekday: any, locale: any = 'fr') =>
    moment().locale(locale).localeData().weekdays()[weekday],
  getFirstDayOfWeek: (locale: any = 'fr') =>
    moment().locale(locale).localeData().firstDayOfWeek(),
  getMonths: (locale: any = 'fr') => {
    const months = [];
    for (let i = 0; i < 12; i += 1) {
      months.push(moment().locale(locale).month(i).format('MMMM'));
    }
    return months;
  },
  formatDate: (
    date: any,
    format: any = FRENCH_DATE_FORMAT,
    locale: any = 'fr',
  ) => m(date, locale).format(format),
  parseDate: (
    str: any,
    format: any = FRENCH_DATE_FORMAT,
    locale: any = 'fr',
  ) => {
    const parsed = moment(str, format, locale, true);
    return parsed.isValid() ? parsed.toDate() : undefined;
  },
};

const frenchFormatDate = (date: Date) =>
  moment(date).format(FRENCH_DATE_FORMAT);

const frenchParseDate = (str: string) => {
  const strict = moment(str, FRENCH_DATE_FORMAT, true);
  if (strict.isValid()) return strict.toDate();
  const lenient = moment(str);
  return lenient.isValid() ? lenient.toDate() : null;
};

// Null-safe, timezone-stable value formatter for the Formik-bound date input.
// Two issues with the library default (`date.toISOString()`):
//  1. No null guard -> clearing/typing an invalid date fires onChange(null)
//     and crashes with "Cannot read properties of null (reading 'toISOString')".
//  2. `toISOString()` converts the picker's LOCAL midnight to UTC, which in a
//     UTC+ timezone rolls the calendar date back one day (e.g. 01/01/2025 ->
//     "2024-12-31T23:00:00Z"). The backend takes the date part, so the entry
//     lands on the wrong day / wrong fiscal year.
// Fix: emit the local calendar date pinned to UTC midnight so the day never
// shifts, and return null when there is no date.
const safeFormFormatDate = (date: Date | null) =>
  date ? `${moment(date).format('YYYY-MM-DD')}T00:00:00.000Z` : null;

const frenchLocale = {
  locale: 'fr',
  localeUtils: MomentLocaleUtils,
  formatDate: frenchFormatDate,
  parseDate: frenchParseDate,
  placeholder: FRENCH_DATE_FORMAT,
};

// Blueprint's DateInput defaults to a narrow [today-... , end-of-year] range,
// which rejects legitimate future dates (e.g. an attestation de régularité
// fiscale valid until next year → "Out of range"). Widen it to ±. Callers can
// still override via props since these come before the {...props} spread.
const DEFAULT_MIN_DATE = moment()
  .subtract(20, 'years')
  .startOf('year')
  .toDate();
const DEFAULT_MAX_DATE = moment().add(10, 'years').endOf('year').toDate();

export const LocalizedDateInput = (props: any) => (
  <BPDateInput
    minDate={DEFAULT_MIN_DATE}
    maxDate={DEFAULT_MAX_DATE}
    {...props}
    {...frenchLocale}
  />
);

export const LocalizedFDateInput = (props: any) => (
  <BPFormikDateInput
    formFormatDate={safeFormFormatDate}
    minDate={DEFAULT_MIN_DATE}
    maxDate={DEFAULT_MAX_DATE}
    {...props}
    {...frenchLocale}
  />
);

export const LocalizedDateRangeInput = (props: any) => (
  <BPDateRangeInput {...props} {...frenchLocale} />
);
